import { Box, HStack, Input, Stack, Text, Textarea } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { z } from "zod";

import {
  getCompanyProfile,
  updateCompanyProfile,
} from "../../../api/company-knowledge";
import { Button } from "../../../components/button";
import PageBody from "../../../components/page-body";
import { toaster } from "../../../components/toaster";
import { useLoading } from "../../../hooks";
import { CompanyProfile } from "../../../types/company-knowledge";

// Labels for the free-text fields come from the original spec comments
// in audiences/components/company-profile.rkt, which were edited out
// alongside the migration; the wording below should be re-checked at
// review.

const companyProfileSchema = z.object({
  name: z.string().trim().min(1, "A name is required."),
  "product-description": z.string(),
  "website-url": z
    .string()
    .refine(
      (value) => value === "" || /^https?:\/\//.test(value),
      "Enter a valid URL (starting with http:// or https://), or leave it blank.",
    ),
  "product-strengths": z.string(),
  "product-limitations": z.string(),
  "common-misconceptions": z.string(),
  "default-markets": z.string(),
  "default-languages": z.string(),
  "additional-context": z.string(),
});

type CompanyProfileFormData = z.infer<typeof companyProfileSchema>;

export default function CompanyProfilePage() {
  const { workspace: workspaceSlug } = useParams();
  const [profile, setProfile] = React.useState<CompanyProfile | null>(null);
  const [, withLoading] = useLoading<void>(true);
  const [isSaving, withSaving] = useLoading<void>(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CompanyProfileFormData>({
    defaultValues: emptyProfile,
    resolver: zodResolver(companyProfileSchema),
  });

  React.useEffect(() => {
    if (!workspaceSlug) return;
    void withLoading(async () => {
      setProfile(await getCompanyProfile(workspaceSlug));
    });
  }, [withLoading, workspaceSlug]);

  React.useEffect(() => {
    if (!profile) return;
    reset(toFormData(profile));
  }, [profile, reset]);

  const onSubmit = handleSubmit(async (data) => {
    if (!workspaceSlug) return;
    await withSaving(async () => {
      await updateCompanyProfile(workspaceSlug, data);
      toaster.create({ type: "success", title: "Company profile updated" });
    });
  });

  return (
    <PageBody>
      <KnowledgeCard
        title="Company & Product"
        sub="Who you are and what you sell."
      >
        <form onSubmit={onSubmit}>
          <Stack gap="1">
            <KnowledgeField
              label="Name"
              error={errors.name?.message}
              input={
                <Input
                  {...register("name")}
                  placeholder="Your company's name"
                />
              }
            />
            <KnowledgeField
              label="What does your company sell?"
              error={errors["product-description"]?.message}
              input={
                <Textarea
                  {...register("product-description")}
                  placeholder="Describe your products or services, who they help, and the important context."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Company website"
              error={errors["website-url"]?.message}
              input={
                <Input
                  {...register("website-url")}
                  placeholder="https://example.com"
                />
              }
            />
            <KnowledgeField
              label="Where your product is strongest"
              error={errors["product-strengths"]?.message}
              input={
                <Textarea
                  {...register("product-strengths")}
                  placeholder="The situations, capabilities, or outcomes you can confidently lead with."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Limitations and tradeoffs"
              error={errors["product-limitations"]?.message}
              input={
                <Textarea
                  {...register("product-limitations")}
                  placeholder="Where your product is less suitable, or where an alternative may be stronger."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Common misunderstandings and corrections"
              error={errors["common-misconceptions"]?.message}
              input={
                <Textarea
                  {...register("common-misconceptions")}
                  placeholder="What people often assume incorrectly, and what is actually true."
                  rows={3}
                />
              }
            />
            <KnowledgeField
              label="Usual markets"
              error={errors["default-markets"]?.message}
              input={
                <Input
                  {...register("default-markets")}
                  placeholder="United States, Canada"
                />
              }
            />
            <KnowledgeField
              label="Usual languages"
              error={errors["default-languages"]?.message}
              input={
                <Input
                  {...register("default-languages")}
                  placeholder="en, es"
                />
              }
            />
            <KnowledgeField
              label="Other important context"
              error={errors["additional-context"]?.message}
              input={
                <Textarea
                  {...register("additional-context")}
                  placeholder="Anything else the product should know about your company."
                  rows={3}
                />
              }
            />
            <HStack marginTop="4">
              <Button loading={isSaving} type="submit" variant="solid">
                Update
              </Button>
            </HStack>
          </Stack>
        </form>
      </KnowledgeCard>
    </PageBody>
  );
}

const emptyProfile: CompanyProfileFormData = {
  name: "Unnamed Company",
  "product-description": "",
  "website-url": "",
  "product-strengths": "",
  "product-limitations": "",
  "common-misconceptions": "",
  "default-markets": "",
  "default-languages": "",
  "additional-context": "",
};

// The schema's fields are nullable strings; empty strings are the
// form's representation of "unset".
const toFormData = (profile: CompanyProfile): CompanyProfileFormData => ({
  name: profile.name ?? "",
  "product-description": profile["product-description"] ?? "",
  "website-url": profile["website-url"] ?? "",
  "product-strengths": profile["product-strengths"] ?? "",
  "product-limitations": profile["product-limitations"] ?? "",
  "common-misconceptions": profile["common-misconceptions"] ?? "",
  "default-markets": profile["default-markets"] ?? "",
  "default-languages": profile["default-languages"] ?? "",
  "additional-context": profile["additional-context"] ?? "",
});

// SettingsCard/SettingsCard equivalent, local to this page for now
// (same shape as frontend/routes/settings/general-page.tsx).
function KnowledgeCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      background="white"
      borderRadius="l3"
      boxShadow="card"
      overflow="hidden"
    >
      <Box
        borderBottomWidth="1"
        borderColor="border.soft"
        paddingX="6"
        paddingY="4"
      >
        <Text color="fg.ink" fontSize="md" fontWeight="medium">
          {title}
        </Text>
        {sub && (
          <Text color="fg.muted" fontSize="sm" marginTop="0.5">
            {sub}
          </Text>
        )}
      </Box>
      <Box paddingBottom="6" paddingTop="5" paddingX="6">
        {children}
      </Box>
    </Box>
  );
}

// FormRow equivalent, local to this page for now.
function KnowledgeField({
  label,
  error,
  input,
}: {
  label: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <Stack gap="1" paddingY="2">
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
      {input}
      {error && (
        <Text color="red.500" fontSize="xs">
          {error}
        </Text>
      )}
    </Stack>
  );
}
